import React, { useState } from "react"
import { Link as RouterLink, useHistory } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { defineMessages, useIntl } from "react-intl"
// Material Ui
import Button from "@material-ui/core/Button"
import Box from "@material-ui/core/Box"
import Divider from "@material-ui/core/Divider"
import IconButton from "@material-ui/core/IconButton"
import ListItemIcon from "@material-ui/core/ListItemIcon"
import ListItemText from "@material-ui/core/ListItemText"
import Menu from "@material-ui/core/Menu"
import MenuItem from "@material-ui/core/MenuItem"
import Typography from "@material-ui/core/Typography"
import Chip from "@material-ui/core/Chip"
import HomeIcon from "@material-ui/icons/Home"

// Icons
import PowerSettingsNew from "@material-ui/icons/PowerSettingsNew"
import GroupAdd from "@material-ui/icons/GroupAdd"
import Person from "@material-ui/icons/Person"
import ArrowDropDown from "@material-ui/icons/ArrowDropDown"
import Widgets from "@material-ui/icons/Widgets"
import AttachFileOutlinedIcon from "@material-ui/icons/AttachFileOutlined"
import DescriptionIcon from "@material-ui/icons/Description"
import MailIcon from "@material-ui/icons/Mail"

// ## //
import { MailOutline } from "@material-ui/icons"
import auth from "../../api/authSlice"
import { checkUserType } from "../userUtils"
import { STAFF } from "../../commons/userTypes"
import MailPreferencesDialog from "../dialog/MailPreferencesDialog"

const messages = defineMessages({
  welcome: {
    defaultMessage: "Welcome, {firstName} {lastName}",
    description: "Welcome message",
    id: "menu.welcome"
  },
  profile: {
    defaultMessage: "Profile",
    id: "menu.profile"
  },
  login: {
    defaultMessage: "Login",
    id: "menu.login"
  },
  logout: {
    defaultMessage: "Logout",
    id: "menu.logout"
  },
  users: {
    defaultMessage: "Users",
    id: "menu.users"
  },
  manageMailPreferences: {
    id: "menu.manageMailPreferences",
    defaultMessage: "Manage mail preferences"
  },
  widgets: {
    defaultMessage: "Pages & Widgets",
    id: "menu.widgets"
  },
  reports: {
    defaultMessage: "Report publication",
    id: "menu.reports"
  },
  forms: {
    defaultMessage: "Create forms",
    id: "menu.forms"
  },
  mails: {
    defaultMessage: "Mails Settings",
    id: "menu.mails"
  }
})

const adminRoutes = [
  {
    path: "/admin/users",
    title: "users",
    icon: <GroupAdd fontSize="small" />
  },
  {
    path: "/admin/pages",
    title: "widgets",
    icon: <Widgets fontSize="small" />
  },
  {
    path: "/admin/reports",
    title: "reports",
    icon: <AttachFileOutlinedIcon fontSize="small" />
  },
  {
    path: "/admin/forms",
    title: "forms",
    icon: <DescriptionIcon fontSize="small" />
  },
  {
    path: "/admin/mail/config/smtp",
    title: "mails",
    icon: <MailIcon fontSize="small" />
  }
]

/**
 * Render Login button when user isn't authenticated
 * @returns {*}
 */
const authenticatedLinks = (login, label) => (
  <Button
    variant="contained"
    color="secondary"
    component={login}
    to="/login"
    disableElevation
  >
    {label}
  </Button>
)

/**
 * Try an implementation using React's hooks style
 */
export default function UserDropdownMenu() {
  // props from global application redux state, more stuff:
  //    > https://react-redux.js.org/next/api/hooks#hooks
  //    > https://thoughtbot.com/blog/using-redux-with-react-hooks
  const dispatch = useDispatch()
  const isAuth = useSelector((state) => state.auth.loggedIn)
  const user = useSelector((state) => state.auth.user)
  const { formatMessage: format } = useIntl()

  // component's state using hooks style
  const [showMenu, setShowMenu] = useState(null)
  const [showMailPreferences, setShowMailPreferences] = useState(false)

  // Router's Link needs a binding with Material-Ui Link component, more stuff:
  //    > https://material-ui.com/guides/composition/#button
  // eslint-disable-next-line react/display-name
  const login = React.forwardRef((props, ref) => (
    <RouterLink innerRef={ref} {...props} />
  ))
  const history = useHistory()

  function handleMenu(event) {
    setShowMenu(event.currentTarget)
  }

  function handleClick(link) {
    setShowMenu(null)
    history.push(link)
  }

  function handleLogout() {
    setShowMenu(null)
    dispatch(auth.actions.logout()).then(() => history.push("/login"))
  }

  function handleShowMailPreferences() {
    setShowMenu(null)
    setShowMailPreferences(true)
  }

  // when user is not logged in display Login button
  if (!isAuth) {
    const localizedButtonLabel = format(messages.login)
    return authenticatedLinks(login, localizedButtonLabel)
  }

  const menuId = "primary-search-account-menu"
  return (
    <>
      {checkUserType(user, STAFF) && (
        <Chip
          className="header-admin-chip"
          label="Admin"
          avatar={<HomeIcon color="primary" />}
          onClick={() => handleClick("/admin")}
        />
      )}

      <Box
        className="header-user-profile"
        display="flex"
        alignItems="center"
        onClick={handleMenu}
      >
        <Typography>
          {format(messages.welcome, {
            firstName: user.firstName.toUpperCase(),
            lastName: user.lastName.toUpperCase()
          })}
        </Typography>
        <IconButton
          size="small"
          color="inherit"
          aria-label="account"
          aria-controls={menuId}
          aria-haspopup="true"
        >
          <ArrowDropDown />
        </IconButton>
      </Box>
      <Menu
        id="menu-appbar"
        anchorEl={showMenu}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right"
        }}
        keepMounted
        transformOrigin={{
          vertical: "top",
          horizontal: "right"
        }}
        open={Boolean(showMenu)}
        onClose={() => setShowMenu(null)}
      >
        {/* AUTH USER ROUTES */}
        <MenuItem onClick={() => handleClick(`/user/${user._id}`)}>
          <ListItemIcon>
            <Person fontSize="small" />
          </ListItemIcon>
          <ListItemText>{format(messages.profile)}</ListItemText>
        </MenuItem>

        <MenuItem onClick={handleShowMailPreferences}>
          <ListItemIcon>
            <MailOutline fontSize="small" />
          </ListItemIcon>
          <ListItemText>{format(messages.manageMailPreferences)}</ListItemText>
        </MenuItem>

        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <PowerSettingsNew fontSize="small" />
          </ListItemIcon>
          <ListItemText>{format(messages.logout)}</ListItemText>
        </MenuItem>

        {/* STAFF ROUTES */}
        {checkUserType(user, STAFF) && <Divider />}
        {checkUserType(user, STAFF) &&
          adminRoutes.map((route) => (
            <MenuItem key={route.title} onClick={() => handleClick(route.path)}>
              <ListItemIcon>{route.icon}</ListItemIcon>
              <ListItemText>{format(messages[route.title])}</ListItemText>
            </MenuItem>
          ))}
      </Menu>

      <MailPreferencesDialog
        open={showMailPreferences}
        onClose={() => setShowMailPreferences(false)}
      />
    </>
  )
}
